import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { Fields, Slot, renderContext, Render, usePuck } from '@puckeditor/core'
import type { PollAnswerProps } from '../PollAnswer/PollAnswer'
import type { PollQuestionProps } from '../PollQuestion/PollQuestion'
import type { PollProductProps } from '../PollProduct/PollProduct'
import { progressBarPropsFromConfig } from '../PollProgressBar/PollProgressBar'
import { StartScreenTransition } from '../shared/StartScreen'
import PollStep from 'steps/poll/PollStep3'
import { getPuckFileUrl } from 'breaktime-theme/src/puck/fields/FileUploadField'
import {
  BackgroundStyleProps,
  BorderStyleProps,
  backgroundStyleField,
  borderStyleField,
  applyBackgroundStyle,
  applyBorderStyle,
  applyTypography,
  splitBackgroundLayers,
} from 'breaktime-theme/src/puck/fields/StyleFields'

// Import poll styles so they're available in the puck iframe for editor preview
import 'steps/shared/quiz-trivia/editorial-trivia-quiz.scss'
import 'steps/personality-quiz/quiz3.scss'
import 'steps/poll/PollStep3.scss'
import 'steps/StartSlideWrapper.scss'

import * as styles from './PollContainer.module.scss'

export type { PollDropdownButtonProps as PollDropdownButton } from '../PollDropdownButton/PollDropdownButton'

export type PollContainerProps = {
  id?: string
  headerImage?: string
  introCopy?: string
  commentQuestion?: string
  questions: Slot
  productPanel: Slot
  nextQuestionButton: Slot
  startScreen: Slot
  beforeCard: Slot
  afterCard: Slot
  afterVote: Slot
  skipButton: Slot
  progressBar: Slot
  background?: BackgroundStyleProps
  cardBackground?: BackgroundStyleProps
  cardBorder?: BorderStyleProps
}

// Slot item shapes (what puck stores in slot arrays)
type PollQuestionSlotItem = {
  type: 'PollQuestion'
  props: PollQuestionProps & {
    answers: PollAnswerSlotItem[]
    headerContent?: any[]
  }
}

type PollAnswerSlotItem = {
  type: 'PollAnswer'
  props: Omit<PollAnswerProps, 'beforePercentage' | 'afterPercentage'> & {
    beforePercentage?: any[]
    afterPercentage?: any[]
  }
}

type PollProductSlotItem = {
  type: 'PollProduct'
  props: PollProductProps
}

type PollProductPanelSlotItem = {
  type: 'PollProductPanel'
  props: { headline?: string; products: PollProductSlotItem[] }
}

type PollNextQuestionButtonSlotItem = {
  type: 'PollNextQuestionButton'
  props: { bgColor: string; textColor: string; bgHoverColor: string }
}

export const POLL_INITIAL_DATA: PollContainerProps = {
  questions: [],
  productPanel: [],
  startScreen: [],
  beforeCard: [],
  afterCard: [],
  afterVote: [],
  skipButton: [],
  progressBar: [
    {
      type: 'PollProgressBar',
      props: {
        activeColor: '#FFFFFF',
        inactiveColor: '#FFFFFF',
        inactiveOpacity: 0.4,
        dotStyle: 'filled',
        size: '13px',
      },
    },
  ],
  background: {},
  cardBackground: {},
  cardBorder: {},
  nextQuestionButton: [
    {
      type: 'PollNextQuestionButton',
      props: {
        bgColor: '#FFF',
        textColor: '#000',
        bgHoverColor: '#704FA0',
        label: 'Next Question',
      },
    },
  ],
}

export const POLL_FIELDS: Fields<PollContainerProps, {}> = {
  questions: {
    type: 'slot',
    allow: ['PollQuestion'],
  },
  productPanel: {
    type: 'slot',
    label: 'Poll Footer',
  },
  nextQuestionButton: {
    type: 'slot',
    allow: ['PollNextQuestionButton'],
  },
  startScreen: {
    type: 'slot',
    label: 'Start Screen',
    allow: ['PollStartScreen'],
  },
  beforeCard: {
    type: 'slot',
    label: 'Before Card',
  },
  afterCard: {
    type: 'slot',
    label: 'After Card',
  },
  afterVote: {
    type: 'slot',
    label: 'After Vote',
  },
  skipButton: {
    type: 'slot',
    label: 'Skip Button',
    allow: ['PollSkipButton'],
  },
  progressBar: {
    type: 'slot',
    label: 'Progress Bar',
    allow: ['PollProgressBar'],
  },
  background: { ...backgroundStyleField, label: 'Background' },
  cardBackground: { ...backgroundStyleField, label: 'Card Background' },
  cardBorder: { ...borderStyleField, label: 'Card Border' },
}

const extractPollDataFromPuckProps = (props: any) => {
  const {
    id,
    questions = [],
    productPanel = [],
    nextQuestionButton = [],
  } = props

  const nqb = (nextQuestionButton as PollNextQuestionButtonSlotItem[]).find(
    i => i.type === 'PollNextQuestionButton',
  )

  return {
    id,
    nextQuestionButtonBgColor: nqb?.props.bgColor ?? '#FFF',
    nextQuestionButtonTextColor: nqb?.props.textColor ?? '#000',
    nextQuestionButtonBgHoverColor: nqb?.props.bgHoverColor ?? '#704FA0',
    questions: (questions as PollQuestionSlotItem[])
      .filter(q => q.type === 'PollQuestion')
      .map((q, qIdx) => ({
        ...q.props,
        id: q.props.id ?? `puck-q-${qIdx}`,
        image: undefined,
        type: 'TEXT' as const,
        answers: ((q.props.answers ?? []) as PollAnswerSlotItem[])
          .filter(a => a.type === 'PollAnswer')
          .map((a, aIdx) => {
            const beforeItems: any[] = a.props.beforePercentage ?? []
            const afterItems: any[] = a.props.afterPercentage ?? []

            // IMAGE layout only when beforePercentage has content
            const hasSlotContent = beforeItems.length > 0

            // Extract image src from an Image component in beforePercentage
            const imageItem = beforeItems.find((i: any) => i.type === 'Image')

            // Extract dropdown button from afterPercentage
            const dbItem = afterItems.find(
              (i: any) => i.type === 'PollDropdownButton',
            )

            return {
              ...a.props,
              id: a.props.id ?? `puck-a-${qIdx}-${aIdx}`,
              type: hasSlotContent ? ('IMAGE' as const) : ('TEXT' as const),
              image: imageItem?.props?.src,
              dropdownButton: dbItem ? dbItem.props : undefined,
            }
          }),
      })),
    productPanel: (() => {
      const panel = (productPanel as PollProductPanelSlotItem[]).find(
        p => p.type === 'PollProductPanel',
      )
      if (!panel) return undefined
      return {
        headline: panel.props.headline,
        products: ((panel.props.products ?? []) as PollProductSlotItem[])
          .filter(p => p.type === 'PollProduct')
          .map((p, i) => {
            const dbSlot: any[] = p.props.dropdownButton ?? []
            const dbItem = dbSlot.find(
              (i: any) => i.type === 'PollDropdownButton',
            )
            const imageUrl = p.props.image
              ? getPuckFileUrl(p.props.image)
              : undefined
            return {
              ...p.props,
              id: p.props.id ?? `puck-prod-${i}`,
              image: imageUrl,
              dropdownButton: dbItem ? dbItem.props : undefined,
            }
          }),
      }
    })(),
  }
}

// Extracted so hooks (useMemo/useCallback) are always called unconditionally.
const PuckPollContainerRenderer = ({ puckCtx }: { puckCtx: any }) => {
  const container = puckCtx.data.content.find(
    (c: any) => c.type === 'PollContainer',
  )
  const containerBackground: BackgroundStyleProps = container?.props?.background ?? {}
  const containerCardBackground: BackgroundStyleProps = container?.props?.cardBackground ?? {}
  const containerCardBorder: BorderStyleProps = container?.props?.cardBorder ?? {}
  const {
    brk,
    step,
    onEngage,
    onAwardPoints,
    onChangeBackground,
    nextStepButton,
  } = (puckCtx.metadata ?? {}) as any

  const breakType: 'editorial' | 'sponsored' =
    puckCtx.data.root?.props?.breakType ?? 'editorial'

  // Memoised slot renderer — rebuilds only when config or breakType changes.
  const slotContent = useCallback(
    (items: any[]): React.ReactNode => {
      if (!items.length) return null
      return (
        <Render
          config={puckCtx.config as any}
          data={{
            content: items,
            root: { props: { breakType } as any },
          }}
          metadata={{ ...puckCtx.metadata, shareEventSlug: 'poll' }}
        />
      )
    },
    [puckCtx.config, breakType, puckCtx.metadata],
  )

  const questionSlotMap = useMemo(() => {
    const map = new Map<string, React.ReactNode>()
    ;(container?.props?.questions ?? []).forEach((q: any, qIdx: number) => {
      const qId = q.props?.id ?? `puck-q-${qIdx}`
      map.set(qId, slotContent(q.props?.headerContent ?? []))
    })
    return map
  }, [slotContent, container])

  const answerSlotMap = useMemo(() => {
    const map = new Map<
      string,
      { before: React.ReactNode; after: React.ReactNode }
    >()
    ;(container?.props?.questions ?? []).forEach((q: any, qIdx: number) => {
      ;(q.props?.answers ?? []).forEach((a: any, aIdx: number) => {
        // Use the same synthetic-id logic as extractPollDataFromPuckProps so
        // answers without an explicit id are never silently dropped (BUG-2).
        const aId = a.props?.id ?? `puck-a-${qIdx}-${aIdx}`
        map.set(aId, {
          before: slotContent(a.props?.beforePercentage ?? []),
          after: slotContent(a.props?.afterPercentage ?? []),
        })
      })
    })
    return map
  }, [slotContent, container])

  const productSlotMap = useMemo(() => {
    const map = new Map<string, React.ReactNode>()
    const panel = (container?.props?.productPanel ?? []).find(
      (p: any) => p.type === 'PollProductPanel',
    )
    ;(panel?.props?.products ?? []).forEach((p: any, pIdx: number) => {
      const pId = p.props?.id ?? `puck-prod-${pIdx}`
      // Inject _productContext so PollDropdownButton uses product-style rendering
      const items = (p.props?.dropdownButton ?? []).map((item: any) =>
        item.type === 'PollDropdownButton'
          ? { ...item, props: { ...item.props, _productContext: true } }
          : item,
      )
      map.set(pId, slotContent(items))
    })
    return map
  }, [slotContent, container])

  const pollData = useMemo(() => {
    const rawPollData = extractPollDataFromPuckProps(container?.props ?? {})
    return {
      ...rawPollData,
      ...(step?.poll?.id ? { id: step.poll.id } : {}),
      questions: rawPollData.questions.map((q: any) => ({
        ...q,
        puckQuestion: true,
        puckHeaderContent: questionSlotMap.get(q.id) ?? null,
        puckTitleStyle: {
          ...applyTypography({
            fontFamily: q.fontFamily,
            fontFamilyFallback: q.fontFamilyFallback,
            textTransform: q.textTransform,
          }),
          ...(q.fontSize ? { fontSize: q.fontSize } : {}),
          ...(q.fontWeight ? { fontWeight: Number(q.fontWeight) || undefined } : {}),
        },
        answers: q.answers.map((a: any) => ({
          ...a,
          puckAnswer: true,
          beforePercentageContent: answerSlotMap.get(a.id)?.before ?? null,
          afterPercentageContent: answerSlotMap.get(a.id)?.after ?? null,
          puckTextStyle: {
            ...applyTypography({
              fontFamily: a.fontFamily,
              fontFamilyFallback: a.fontFamilyFallback,
              textTransform: a.textTransform,
            }),
            ...(a.fontSize ? { fontSize: a.fontSize } : {}),
            ...(a.fontWeight ? { fontWeight: Number(a.fontWeight) || undefined } : {}),
          },
        })),
      })),
      productPanel: rawPollData.productPanel
        ? {
            ...rawPollData.productPanel,
            products: rawPollData.productPanel.products.map((p: any) => ({
              ...p,
              puckDropdownContent: productSlotMap.get(p.id) ?? null,
            })),
          }
        : undefined,
    }
  }, [container, step, questionSlotMap, answerSlotMap, productSlotMap])

  // Render the entire footer slot (PollProductPanel + VideoEmbed + any other components)
  // as one block via <Render />, preserving the editor order.
  // PollProductPanel is production-aware — it reads PuckPollContext to render the real
  // PollProducts component with carousel, buttons and analytics.
  const footerSlotContent = useMemo(() => {
    const items = container?.props?.productPanel ?? []
    if (!items.length) return null
    return slotContent(items)
  }, [slotContent, container])

  const beforeCardContent = useMemo(() => {
    const items = container?.props?.beforeCard ?? []
    if (!items.length) return null
    return slotContent(items)
  }, [slotContent, container])

  const afterCardContent = useMemo(() => {
    const items = container?.props?.afterCard ?? []
    if (!items.length) return null
    return slotContent(items)
  }, [slotContent, container])

  const skipButtonContent = useMemo(() => {
    const items = container?.props?.skipButton ?? []
    if (!items.length) return null
    return slotContent(items)
  }, [slotContent, container])

  const afterVoteContent = useMemo(() => {
    const items = container?.props?.afterVote ?? []
    if (!items.length) return null
    return slotContent(items)
  }, [slotContent, container])

  const progressBarContent = useMemo(() => {
    const items = container?.props?.progressBar ?? []
    if (!items.length) return null
    return slotContent(items)
  }, [slotContent, container])

  const progressBarStyleProps = useMemo(() => {
    const items = (container?.props?.progressBar ?? []) as any[]
    const item = items.find((i: any) => i.type === 'PollProgressBar')
    if (!item) return undefined
    return progressBarPropsFromConfig(item.props)
  }, [container])

  const pollDataWithFooter = useMemo(() => ({
    ...pollData,
    puckProductPanelContent: footerSlotContent,
  }), [pollData, footerSlotContent])

  const bgStyle = applyBackgroundStyle(containerBackground)
  const hasBackground = Object.keys(bgStyle).length > 0

  const bgImageSrc = containerBackground?.backgroundImage
    ? getPuckFileUrl(containerBackground.backgroundImage as any)
    : ''
  const mobileBgImageSrc = containerBackground?.mobileBackgroundImage
    ? getPuckFileUrl(containerBackground.mobileBackgroundImage as any)
    : ''

  const onChangeBackgroundRef = useRef(onChangeBackground)
  useEffect(() => {
    onChangeBackgroundRef.current = onChangeBackground
  }, [onChangeBackground])

  useEffect(() => {
    const cb = onChangeBackgroundRef.current
    if (!cb) return
    if (!hasBackground) return
    cb({
      backgroundColor: containerBackground?.backgroundColor,
      backgroundImageSrc: bgImageSrc || undefined,
      mobileBackgroundImageSrc: mobileBgImageSrc || undefined,
      backgroundSize: containerBackground?.backgroundSize,
      backgroundPosition: containerBackground?.backgroundPosition,
      backgroundRepeat: containerBackground?.backgroundRepeat,
      backgroundOpacity: containerBackground?.backgroundImageOpacity,
    })
  }, [
    hasBackground,
    containerBackground?.backgroundColor,
    bgImageSrc,
    mobileBgImageSrc,
    containerBackground?.backgroundSize,
    containerBackground?.backgroundPosition,
    containerBackground?.backgroundRepeat,
    containerBackground?.backgroundImageOpacity,
  ])

  const cardLayers = splitBackgroundLayers(containerCardBackground)
  const puckCardInlineStyle = {
    ...cardLayers.base,
    ...applyBorderStyle(containerCardBorder),
  }
  const puckCardOverlayStyle = cardLayers.overlay

  const pollStep = (
    <PollStep
      brk={brk}
      step={{
        ...(step ?? {}),
        poll: pollDataWithFooter,
        puckCardInlineStyle,
        puckCardOverlayStyle,
        puckBeforeCardContent: beforeCardContent,
        puckAfterCardContent: afterCardContent,
        puckAfterVoteContent: afterVoteContent,
        puckProgressBarContent: progressBarContent,
        puckProgressBarProps: progressBarStyleProps,
      }}
      onEngage={onEngage ?? (() => {})}
      onAwardPoints={onAwardPoints ?? (() => {})}
      skipButton={skipButtonContent}
      nextStepButton={nextStepButton}
      isPuck={false}
    />
  )

  return (
    <StartScreenTransition
      startScreen={container?.props?.startScreen}
      defaultButtonLabel="Start Poll"
    >
      {pollStep}
    </StartScreenTransition>
  )
}

const PuckPollEditorWrapper = ({
  children,
  background,
  cardBackground,
  cardBorder,
  beforeCard,
  afterCard,
  afterVote,
  skipButton,
  progressBar,
}: PropsWithChildren<{
  background?: BackgroundStyleProps
  cardBackground?: BackgroundStyleProps
  cardBorder?: BorderStyleProps
  beforeCard?: React.ReactNode
  afterCard?: React.ReactNode
  afterVote?: React.ReactNode
  skipButton?: React.ReactNode
  progressBar?: React.ReactNode
}>) => {
  const puck = usePuck()

  const isEditorial =
    puck?.appState?.data?.root?.props?.breakType === 'editorial'

  const cardLayers = splitBackgroundLayers(cardBackground)
  const cardStyle = {
    ...cardLayers.base,
    ...applyBorderStyle(cardBorder),
  }
  const mainLayers = splitBackgroundLayers(background)

  return (
    <main
      className={isEditorial ? 'editorial container' : 'sponsored'}
      style={{ ...mainLayers.base, display: 'flow-root' }}
    >
      {mainLayers.overlay && <div style={mainLayers.overlay} />}
      <div
        className={`step-3-template ${
          isEditorial
            ? 'editorial-step-3 editorial-step-wrapper'
            : 'sponsored-flow-wrapper'
        } step-undefined step-position-0`}
        data-steptype="POLL"
      >
        <section className="poll-step poll-step-3 quiz quiz-integrated">
          <div className="step-content no-question-img poll-question">
            {beforeCard}
            <div
              className={`poll-item no-question-img${
                isEditorial ? '' : ' sponsored'
              }`}
              style={cardStyle}
            >
              {progressBar}
              {cardLayers.overlay && <div style={cardLayers.overlay} />}
              {children}
              {afterVote}
              {skipButton}
            </div>
            {afterCard}
          </div>
        </section>
      </div>
    </main>
  )
}

export const PuckPollContainer = ({
  questions: Questions,
  productPanel: ProductPanel,
  nextQuestionButton: NextQuestionButton,
  startScreen: StartScreen,
  beforeCard: BeforeCard,
  afterCard: AfterCard,
  afterVote: AfterVote,
  skipButton: SkipButton,
  progressBar: ProgressBar,
  background,
  cardBackground,
  cardBorder,
}: {
  questions: any
  productPanel: any
  nextQuestionButton: any
  startScreen: any
  beforeCard: any
  afterCard: any
  afterVote: any
  skipButton: any
  progressBar: any
  id?: string
  headerImage?: string
  introCopy?: string
  commentQuestion?: string
  background?: BackgroundStyleProps
  cardBackground?: BackgroundStyleProps
  cardBorder?: BorderStyleProps
}) => {
  const puckCtx = useContext(renderContext)
  const isRenderMode = puckCtx.data.content.length > 0

  if (isRenderMode) {
    return <PuckPollContainerRenderer puckCtx={puckCtx} />
  }

  // EDITOR MODE: replicate the real break player wrapper structure for accurate styling
  return (
    <PuckPollEditorWrapper
      background={background}
      cardBackground={cardBackground}
      cardBorder={cardBorder}
      beforeCard={<BeforeCard className={styles.pollBeforeCardSlot} minEmptyHeight={32} />}
      afterCard={<AfterCard className={styles.pollAfterCardSlot} minEmptyHeight={32} />}
      afterVote={<AfterVote className={styles.pollAfterVoteSlot} minEmptyHeight={32} />}
      skipButton={<SkipButton allow={['PollSkipButton']} className={`${styles.pollSkipButtonSlot} no-slot-padding`} minEmptyHeight={32} />}
      progressBar={<ProgressBar allow={['PollProgressBar']} className="no-slot-padding" />}
    >
      <StartScreen allow={['PollStartScreen']} minEmptyHeight={80} className={styles.pollStartScreenSlot} />
      <Questions
        style={{ display: 'flex', flexDirection: 'column' }}
        allow={['PollQuestion']}
        className={styles.pollQuestionsSlot}
      />
      <NextQuestionButton allow={['PollNextQuestionButton']} className="no-slot-padding" />
      <ProductPanel className="poll-product-panel-slot" minEmptyHeight={48} />
    </PuckPollEditorWrapper>
  )
}
